<template>
  <div class="login-page">
    <div class="login-frame">
      <div class="side-section" :class="['bg-' + bgRandom]">
        <div class="mask"></div>
        <div class="info">
          <h1>Welcome to BlueMessaging's Platform</h1>
          <div class="social-links">
            <a href="https://twitter.com/bluemessaging" target="_blank"
              ><i class="fab fa-twitter"></i
            ></a>
            <a
              href="https://www.linkedin.com/company/bluemessaging?trk=mini-profile"
              target="_blank"
              ><i class="fab fa-linkedin"></i
            ></a>
            <a
              href="https://www.facebook.com/BlueMessaging-122063917939324/"
              target="_blank"
              ><i class="fab fa-facebook"></i
            ></a>
          </div>

          <div class="version-info">
            {{ versionLabel }}
          </div>
        </div>
      </div>
      <div class="form-section">
        <transition name="form-section__transition">
          <form v-show="!showUnlockSessions" @submit.prevent="tryLogin">
            <img class="bm-logo" src="@/assets/img/bm-logo-color.png" />
            <section class="form-section__inputs">
              <b-input-group class="mt-0">
                <b-input-group-prepend is-text class="input-icon">
                  <i class="fa fa-slash flip-v"></i>
                </b-input-group-prepend>
                <b-form-input
                  placeholder="Dominio"
                  v-model="domain"
                  autocomplete="true"
                  autofocus
                  @keyup="toUser"
                  @change="clearFeedback"
                  ></b-form-input>
              </b-input-group>
              <b-input-group class="mt-2">
                <b-input-group-prepend is-text class="input-icon">
                  <i class="fa fa-user-circle"></i>
                </b-input-group-prepend>
                <b-form-input
                  id="form-user"
                  placeholder="Usuario"
                  v-model="user"
                  autocomplete="true"
                  @keyup="toPassword"
                  @change="clearFeedback"
                  ></b-form-input>
              </b-input-group>
              <b-input-group class="mt-2">
                <b-input-group-prepend is-text class="input-icon">
                  <i class="fa fa-key"></i>
                </b-input-group-prepend>
                <b-form-input
                  id="form-password"
                  placeholder="Contraseña"
                  v-model="password"
                  type="password"
                  @keyup="toLoginButton"
                  @change="clearFeedback"
                  ></b-form-input>
              </b-input-group>
              <b-alert class="mt-1 mb-0 small" variant="danger" :show="feedback !== ''">
                {{feedback}}
              </b-alert>
              <button
                id="button-login"
                type="submit"
                :disabled="!filledRequiredCredentials || loadingLogin"
                class="btn bm-bg-color"
              >
                <div v-if="!loadingLogin">Ingresar</div>
                <div v-else class="d-flex justify-content-center align-items-center">
                  <div class="spinner-border spinner-border-sm"></div>
                </div>
              </button>
            </section>
            <button type="button" class="btn btn-light btn-sm" @click="redirectToRecoveryPassword" title="Recuperar contraseña">¿Olvidaste tu contraseña?</button>
          </form>
        </transition>
        <!-- Section to unlock sessions -->
        <transition name="unlock-sessions-fade-in">
          <UnlockSessions v-show="showUnlockSessions"
            :domain="domain"
            :username="user"
            :password="password"
            :loadSessions="showUnlockSessions"
            @close-unlock-sessions="closeUnlockSessions"
          >
            <template slot="unlock-sessions__error-message">
              <div v-if="feedback" class="alert alert-danger mt-1 mb-0 small text-center">
                {{feedback}}
              </div>
            </template>
          </UnlockSessions>
        </transition>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import authService from '@/services/support/AuthService'
import { GrantType, LoginRequest, LoginResponse, RecoveryReasons, SourceDeviceType } from '../types/Login'
import CookiesService from '@/services/support/CookiesService'
import requestRPCPlatform from '@/services/PlatformRPC'
import router from '@/router'
import { LoginErrorDescriptionsTranslate } from '@/types/ErrorDictionary'
import userInfoStore from '@/store/userInfo'
import { redirectToPlatformIndex, removeJWTValues, revokeTokens } from '@/api'
import DeviceInfo from '@/store/deviceInfo'
import config from '@/config'
import UnlockSessions from '@/components/login/UnlockSessions.vue'
import { COOKIES } from '@/types/Config'
import localStorageUser from '@/store/localStoreUser'
import FillgridService from '@/services/FillgridService'
import LogService from '@/services/support/LogService'
import { APIErrorFormat } from '@/types/Errors'
import { RoleNames } from '@/types/Auth'

@Component({
  components: {
    UnlockSessions
  }
})
export default class Login extends Vue {
  OVERPASS_LICENSES_LIMIT_CODE = 2607 // In case this code change in the response in the API, it must be updated here too.
  bgRandom = 0
  loadingLogin = false
  domain: string = ''
  user: string = ''
  password: string = ''
  feedback: string = ''
  versionLabel: string = ''
  INVALID_PLATFORM_ROLES: string[] = [
    RoleNames.MOBILE
  ]
  showUnlockSessions: boolean = false

  created () {
    this.bgRandom = Math.floor((Math.random() * 5) + 1)
    this.getEnviromentSettings()
    this.autoFillFields()
  }
  mounted () {
    localStorageUser.removeUsersAndActiveUsersLocalStorage()
  }
  /**
   * Validate if all the required input were filled
   */
  get filledRequiredCredentials (): boolean {
    return this.domain.length > 0 &&
      this.user.length > 0 &&
      this.password.length > 0
  }
  /**
   * Tries to fill the domain and user field
   * by the cookie 'last-login'
   */
  autoFillFields () {
    let loginCookie = CookiesService.get(COOKIES.LAST_LOGIN)
    if (loginCookie) {
      let loginCookieSplit = loginCookie.split('@')
      this.domain = loginCookieSplit[1]
      this.user = loginCookieSplit[0]
      this.focusById('form-password')
    }
  }

  /**
   * Set the 'value' to the 'loadingLogin' variable
   * @param value to assign to the variable
   */
  setLoadingLogin (value: boolean): void {
    this.loadingLogin = value
  }
  /**
   * Initializes the process to create a new token
   */
  tryLogin () {
    if (this.loadingLogin) return
    this.clearFeedback()
    this.setLoadingLogin(true)
    this.createToken()
  }

  /**
   * Based on the domain, user and password,
   * prepare the token request and make the request.
   */
  createToken () {
    this.prepareTokenRequest(this.domain, this.user, this.password)
      .then((tokenRequest) => {
        this.requestToken(tokenRequest)
      })
      .catch((error) => {
        LogService.error('Error on prepare token request. ' + error)
      })
  }

  /**
   * Requests a token with the user credentials and
   * check if the token is valid (no need reset pwd)
   * @param token to request the token
   */
  requestToken (token: LoginRequest) {
    authService.getDeviceToken(token).then((response) => {
      this.setLastLoginCookie(this.user, this.domain)
      this.validateNeedResetPwd(response)
    }).catch((error) => {
      this.handleError(error)
      LogService.error('Error on request token. ' + error)
    })
  }

  /**
   * Checks if the token does not need to reset pwd and
   * init the user credentials in the store
   * @param token response to validate
   */
  validateNeedResetPwd (token: LoginResponse) {
    if (token && token satisfies LoginResponse) {
      if (token.needResetpwd) {
        const params: { recoveryReason: RecoveryReasons } = { recoveryReason: 'expirationPwd' }
        router.push({ name: 'recovery-password', params })
      } else {
        authService.setAuthCookies(token)
        this.initUserCredentials()
      }
    } else {
      this.setLoadingLogin(false)
      LogService.error('Token doesn\'t have a valid schema. ')
    }
  }

  /**
   * Create the 'last-login' token with the value '<userName>@<domainName>'
   * @param user user name of the user that attempt login
   * @param domain domain name of the user that attempt login
   */
  setLastLoginCookie (user: string, domain: string): void {
    CookiesService.set(
      COOKIES.LAST_LOGIN, // name
      `${user}@${domain}`, // value
      '12h', // expires / age
      '/', // path
      config.appDomain, // domain
      config.isProduction // secure flag
    )
  }

  /**
   * Set the JWT user tokens, initialize the user credentials for the app,
   * validate if the user has a valid role and create the RPC credentials
   */
  initUserCredentials () {
    authService.initAuth()
      .then(() => {
        if (this.hasUserPlatformRole) {
          this.generateRPCCredentialsAndRedirectionToindex()
          FillgridService.clearCustomSearchInLocalStorage()
        } else {
          this.deleteInvalidUserToken()
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.invalid_role)
        }
      }).catch((error) => {
        this.handleError(error)
        LogService.error('Error on initializing user credentials. ' + error)
      })
  }

  /**
   * Handles the case when the user has a invalid role for platform
   * revokes the token created and removes the token cookies
   */
  async deleteInvalidUserToken () {
    await revokeTokens()
    removeJWTValues()
    this.setLoadingLogin(false)
  }

  /**
   * Checks if the user in the store has a valid role for platform.
   */
  get hasUserPlatformRole (): boolean {
    return !this.INVALID_PLATFORM_ROLES.includes(userInfoStore.userInfo.role.name)
  }

  /**
   * Make a request ro create the user credentials for platform.
   * If everything is ok, this redirect to platform
   */
  generateRPCCredentialsAndRedirectionToindex () {
    this.loginByParameters()
      .then(() => {
        // If everything is ok, redirect to platform index
        redirectToPlatformIndex()
      })
      .catch((error) => {
        this.setLoadingLogin(false)
        this.handleError(error)
        this.deleteInvalidUserToken()
        LogService.error('Error on login in platform. ' + error)
      })
  }

  /**
   * Handles the error for the API or another kind during trying login
   * @param error to handle
   */
  handleError (error : any) {
    try {
      const apiError = APIErrorFormat.getInstance(error.response.data)
      let errorCode
      let originalErrorDescription = 'Error'
      let status

      if (apiError.isNewErrorFormat()) {
        errorCode = apiError.errors[0].code
        originalErrorDescription = apiError.errors[0].description
        status = apiError.httpError?.status
      }

      if (errorCode !== undefined) {
        // Errors with a code number
        if (errorCode === 2013) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.over_attemps)
        } else if (errorCode === 2600) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.incorrect_values)
        } else if (errorCode === 2601) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.status_invalid)
        } else if (errorCode === this.OVERPASS_LICENSES_LIMIT_CODE) {
          // Manage the error to enable the unlock sessions. 2607
          this.setFeedbackMessage(
            LoginErrorDescriptionsTranslate.active_license ||
            originalErrorDescription
          )
          this.setShowUnlockSessions(true)
        } else if (errorCode === 2608) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.device_ressetle)
        } else if (errorCode === 2640) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.invalid_role)
        } else if (errorCode === 3247) {
          // Error 3247: You must change your password
          this.setFeedbackMessage('Cambio de contraseña requerido')
          const params: { recoveryReason: RecoveryReasons } = { recoveryReason: 'expirationPwd' }
          this.redirectToRecoveryPassword(params)
        } else if (errorCode === 2016) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.domain_temporarily_closed)
          revokeTokens()
          removeJWTValues()
        } else {
          this.setFeedbackMessage('Problema al iniciar sesión.')
        }
      } else if (status) {
        // General errors by API
        if (status === 500) {
          this.setFeedbackMessage('Problema al iniciar sesión. Internal server error.')
        } else {
          this.setFeedbackMessage('Problema al iniciar sesión. ' + LoginErrorDescriptionsTranslate.undefined_error)
        }
      }
    } catch (error) {
      this.setFeedbackMessage('Problema al iniciar sesión. ' + LoginErrorDescriptionsTranslate.undefined_error)
      LogService.error('Error on handleError. ' + error)
    } finally {
      this.setLoadingLogin(false)
    }
  }

  /**
   * Prepares a login request with the login form values
   *
   * @param domain the domain of the user
   * @param user the name of the user
   * @param password the password of the user
   * @returns LoginRequest with the values to create tokens
   */
  async prepareTokenRequest (domain: string, user: string, password: string) {
    try {
      let loginData = new LoginRequest()
      loginData.domain = domain
      loginData.user = user
      loginData.password = password
      loginData.uuid = await DeviceInfo.getFingerprint()
      loginData.device = await DeviceInfo.getBrowserName()
      loginData.grantType = GrantType.PASSWORD_DEVICE
      loginData.sourceDevice = SourceDeviceType.WEB
      return loginData
    } catch (error) {
      LogService.error('Error on prepare token request. ' + error)
      return Promise.reject(error)
    }
  }

  /**
   * Sets the content to the login feedback
   *
   * @param msg to assign
   */
  setFeedbackMessage (msg: string) {
    this.feedback = msg
  }
  /**
   * Gets the enviroment variables by platform API
   * and assign the class to body element
   */
  async getEnviromentSettings () {
    await requestRPCPlatform('getEnvironmentSettings', [])
      .then((response: any) => {
        let body = document.querySelector('body')
        this.versionLabel = 'BMP Versión 3.1.0 - Revisión ' + response.revisionVerion
        if (response.environmentName === 'PROD') {
          body!.classList.add('prod')
        } else if (response.environmentName === 'STAGE') {
          body!.classList.add('stg')
        } else if (response.environmentName === 'DEV') {
          body!.classList.add('dev')
        }
      })
      .catch((e) => {
        LogService.error('Error on getting settings env. ' + e)
      })
  }
  /**
   * Calls the loginByParamenters method from platform api
   * and it generates the required cookies to login in platform.
   */
  async loginByParameters () {
    return requestRPCPlatform(
      'loginByParameters',
      [this.domain, this.user, this.password]
    ).then(() => {
      return Promise.resolve()
    }).catch((e) => {
      LogService.error('Error on login into platform. ' + e)
      return Promise.reject(e)
    })
  }
  /** Redirects to recovery password view */
  redirectToRecoveryPassword (params?: { recoveryReason: RecoveryReasons }) {
    router.push({ name: 'recovery-password', params })
  }
  /** Detects when the key down is enter */
  keydownEnter (event: any) {
    event.preventDefault()
    return event.key === 'Enter'
  }
  /**
   * Focus the 'name' input
   * @param name - name input to focus
  */
  focusById (name: string) {
    let element = document.getElementById(name)
    if (element) element.focus()
  }
  /** Focus the user input */
  toUser (event: any) {
    if (this.keydownEnter(event)) {
      this.focusById('form-user')
    }
  }
  /** Focus the password input */
  toPassword (event: any) {
    if (this.keydownEnter(event)) {
      this.focusById('form-password')
    }
  }
  /** Focus the login button */
  toLoginButton (event: any) {
    if (this.keydownEnter(event)) {
      this.focusById('button-login')
    }
  }
  /** Clears the feedback message */
  clearFeedback () {
    this.setFeedbackMessage('')
  }
  /**
   * Set a new 'value' to the 'showUnlockSessions' variable
   *
   * @param value to set
   */
  setShowUnlockSessions (value: boolean): void {
    this.showUnlockSessions = value
  }
  /**
   * Close the unlock sessions component when recieve the emit from it
   */
  closeUnlockSessions (): void {
    this.setShowUnlockSessions(false)
    this.setFeedbackMessage('')
  }
}
</script>

<style lang="scss" scoped>

body.dev .bm-logo, body.stg .bm-logo {
  filter: grayscale(100%);
}

body .bm-bg-color,
body .bm-bg-color.disabled:hover,
body .login-page .login-frame .side-section .mask {
  background-color: var(--primary);
}

body .bm-bg-color:hover,
body .bm-bg-secondary {
  background-color: #2d7cb1;
}

body.stg .bm-bg-color,
body.stg .bm-bg-color.disabled:hover,
body.stg .login-page .login-frame .side-section .mask {
    background-color: #039c56;
}

body.stg .bm-bg-color:hover,
body.stg .bm-bg-secondary {
  background-color: #026a3a;
}

body.stg .font-color-primary {
  color: #039c56;
}

body.dev .bm-bg-color,
body.dev .bm-bg-color.disabled:hover,
body.dev .login-page .login-frame .side-section .mask {
  background-color: #b33c3c;
}

body.dev .bm-bg-color:hover,
body.dev .bm-bg-secondary {
  background-color: #8d2f2f;
}

body.dev .font-color-primary {
  color: #b33c3c;
}

.login-page {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: #f0f4f3;
  padding: 5% 0;
  overflow-x: hidden;
}

.login-page * {
  box-sizing: border-box;
}

.login-page .login-frame {
  margin: 0 auto;
  width: 80%;
  min-width: 320px;
  max-width: 740px;
  height: 50%;
  min-height: 525px;
  max-height: 700px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 5px 15px -2px #ccc;
}

.login-page .login-frame .side-section {
  position: relative;
  float: left;
  width: 42%;
  height: 100%;
  background: #fff;
  background-size: cover;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

.login-page .login-frame .side-section.bg-1 {
  background: url(~@/assets/img/login/login-info-section-bg-1.jpg);
  background-size: cover;
  background-repeat: no-repeat;
}

.login-page .login-frame .side-section.bg-2 {
  background: url(~@/assets/img/login/login-info-section-bg-2.jpg);
  background-size: cover;
  background-repeat: no-repeat;
}

.login-page .login-frame .side-section.bg-3 {
  background: url(~@/assets/img/login/login-info-section-bg-3.jpg);
  background-size: cover;
  background-repeat: no-repeat;
}

.login-page .login-frame .side-section.bg-4 {
  background: url(~@/assets/img/login/login-info-section-bg-4.jpg);
  background-size: cover;
  background-repeat: no-repeat;
}

.login-page .login-frame .side-section.bg-5 {
  background: url(~@/assets/img/login/login-info-section-bg-5.jpg);
  background-size: cover;
  background-repeat: no-repeat;
}

.login-page .login-frame .side-section .mask {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  opacity: 0.8;
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
  /* background-color: var(--primary); */
}

.login-page .login-frame .side-section .info {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 20px;
}

.login-page .login-frame .side-section .info h1 {
  color: #fff;
  font-size: 18px;
  font-weight: lighter;
  text-align: right;
  line-height: 25px;
}

.login-page .login-frame .side-section .info .social-links {
  text-align: right;
  color: #fff;
  font-size: 28px;
}

.login-page .login-frame .side-section .info .social-links a {
  padding: 0 0 0 8px;
  text-decoration: none;
  color: #fff;
  margin-left: 3px;
}

.login-page .login-frame .side-section .info .social-links a i {
  opacity: 0.6;
}

.login-page .login-frame .side-section .info .social-links a:hover i {
  opacity: 1;
}

.login-page .login-frame .side-section .info .version-info {
  position: absolute;
  left: 10px;
  bottom: 10px;
  color: #fff;
  font-size: 14px;
  opacity: 0.6;
}

.login-page .login-frame .form-section {
  position: relative;
  float: right;
  width: 58%;
  height: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 2rem;
    img {
      width: 80%;
      margin-bottom: 25px;
    }
    .form-section__inputs {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 1rem;
      width: 100%;
      .input-icon {
        width: 40px;
      }
      & > #button-login {
        height: 44px;
        min-width: 160px;
        margin: 8px 0 0 0;
        padding: 12px 32px;
        font-size: 16px;
        text-transform: uppercase;
        box-shadow: 0 4px 6px #ccc;
        color: #fff;
      }
    }
    .recovery-password {
      position: relative;
      width: 150px;
      margin: 1rem;
      & > p {
        font-size: 12px;
        margin: 0;
        padding: 2px;
        border-radius: 4px;
        -webkit-user-select: none; /* Safari */
        -ms-user-select: none; /* IE 10 and IE 11 */
        user-select: none; /* Standard syntax */
        &:hover {
          background-color: #f0f4f3bb;
          box-shadow: 0 1px 4px 0px #a7a7a7;
          transition-duration: 0.3s;
        }
      }
    }
  }
}
/* Unlock sessions  */
.form-section__transition-enter-active,
.form-section__transition-leave-active,
.unlock-sessions-fade-in-enter-active,
.unlock-sessions-fade-in-leave-active {
  transition: transform 0.4s ease-out;
}
.unlock-sessions-fade-in-enter,
  .unlock-sessions-fade-in-leave-to {
  transform: translateY(100%);
}
.form-section__transition-enter,
.form-section__transition-leave-to {
  transform: translateY(-100%);
}

@media screen and (max-width: 700px) {
  .login-page .login-frame {
    max-height: 750px;
  }
  .login-page .login-frame .side-section {
    width: 100%;
    height: 125px;
    border-top-left-radius: 12px;
    border-bottom-left-radius: 0;
    border-top-right-radius: 12px;
    border-bottom-right-radius: 0;
  }
  .login-page .login-frame .side-section .mask {
    border-top-left-radius: inherit;
    border-bottom-left-radius: inherit;
    border-top-right-radius: inherit;
    border-bottom-right-radius: inherit;
  }
  .login-page .login-frame .form-section {
    width: 100%;
    height: 400px;
    form {
      width: 100%;
      img {
        width: 50%;
        min-width: 180px;
        max-width: 300px;
        margin: 0;
      }
      .recovery-password {
        margin: 0;
      }
    }
  }
}
@media screen and (max-width: 400px) {
  .login-page .login-frame .form-section form img {
    width: 40%;
    margin: 0;
  }
  .login-page .login-frame .side-section .info .version-info {
    font-size: 11px;
    font-weight: normal;
  }
  .login-page .login-frame .form-section form .recovery-password {
    margin: 0;
  }
}
</style>
